/**
 * @file        Homepage.service.ts
 * @description Cypher hompage service
 * @version     2.0.0
 * @author      Mehran Majidi
 * @copyright   Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *              Property of MindShare HDV LLC.  This file cannot be used or
 *              altered in anyway without expressed written permission from
 *              MindShare HDV LLC.
 */
// Angular libraries
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { CyAccessService } from "./cyAccess.service";
import { PreferenceService } from "./preference.service";
import { environment } from "@environments/environment";
import
{
    HOME_PAGE_VALUE,
    HOME_PAGE_ROUTE,
    HOME_TAG
}
    from '@app/constants/Setting.constant';

@Injectable
({
    providedIn: 'root'
})
class HomepageService
{
    //_____________________________ C O N S T R U C T O R ______________________
    /**
     * @desc   - service constructor
     * @access - public
     * @param  - CyAccessService cyAccessService:
     *           CyAccessService
     * @param  - PreferenceService preferenceService:
     *           PreferenceService
     * @param  - Router router: Angular Router
     * @return -
     */
    constructor
    (
        private cyAccessService: CyAccessService,
        private preferenceService: PreferenceService,
        private router: Router
    ){}

    //______________________ S T A T I C  M E T H O D S ________________________

    //__________________ P R I V A T E  M E T H O D S __________________________
    /**
     * @desc   - submits the cyAccess form to cypherCourt
     * @access - private
     * @param  - uId: String
     * @return -
     */
    private async submitCyAccess
    (
        uId: String
    )
    {
        let accessKey;
        let cyAccessResponse;
        let fallbackResponse;
        let credentials;
        let form;
        let input;
        let courtLoginUrl;

        courtLoginUrl = this.getCourtLoginUrl();
        cyAccessResponse = null;
        fallbackResponse = null;

        try
        {
            cyAccessResponse =
                await this.cyAccessService.addUpdateCyAccess(uId);
        }
        catch (error)
        {
            cyAccessResponse = null;
        }

        accessKey = cyAccessResponse?.cyData?.accessKey || '';

        if (!accessKey)
        {
            try
            {
                fallbackResponse =
                    await firstValueFrom(
                        this.cyAccessService
                            .createCaseManagementSession(uId)
                    );
                accessKey =
                    fallbackResponse?.cyData?.accessKey || '';
            }
            catch (error)
            {
                accessKey = '';
            }
        }

        if (!accessKey)
        {
            window.location.href = courtLoginUrl;
            return;
        }

        credentials =
        {
            accessKey: accessKey,
            uId: uId,
            pageAction: 'CyAccess'
        }

        form = document.createElement('form');

        form.method = 'POST';
        form.action = courtLoginUrl;
        form.style.display = 'none';

        // Create input fields for each credential
        Object.keys(credentials).forEach(key =>
        {
            input = document.createElement('input');

            input.type = 'hidden';
            input.name = key;
            input.value = credentials[key];
            form.appendChild(input);
        });

        // Append the form to the body and submit it
        document.body.appendChild(form);
        form.submit();
    }

    /**
     * @desc   - build cypherCourt login URL
     * @access - private
     * @param  -
     * @return - string
     */
    private getCourtLoginUrl
    (
    ): string
    {
        let courtUrl;
        let trimmedCourtUrl;
        let loginUrl;

        courtUrl = environment.courtUrl || '';
        trimmedCourtUrl = courtUrl.replace(/\/+$/, '');
        loginUrl = `${trimmedCourtUrl}/login.php`;

        return loginUrl;
    }

    /**
     * @desc   - get the webapp route for a home
     *           preference value
     * @access - private
     * @param  - string homePreference : home
     *           preference value
     * @return - string : Angular route path or null
     */
    private getWebappRoute
    (
        homePreference: string
    ): string
    {
        let routeMap: { [key: string]: string };
        let result: string;

        routeMap =
        {
            [HOME_PAGE_VALUE.VIN_MODULE]:
                'inspectionsCalendar',
            [HOME_PAGE_VALUE.CASE_QUEUE]:
                'caseQueue',
            [HOME_PAGE_VALUE.CASE_PROCCESS]:
                'case',
            [HOME_PAGE_VALUE.COURT_CALENDAR]:
                'courtCalendar',
            [HOME_PAGE_VALUE.DASHBOARD]:
                'dashboard',
            [HOME_PAGE_VALUE.CITATIONS]:
                'citations',
            [HOME_PAGE_VALUE.REPORT]:
                'analytic',
            [HOME_PAGE_VALUE.PREFERENCE]:
                'settings'
        };

        result = routeMap[homePreference] || null;

        return result;
    }

    //___________________ P U B L I C  M E T H O D S ___________________________
    /**
     * @desc   - proccess the homepage redirection.
     *           Routes based on home preference:
     *           CYPHER_WEBAPP routes navigate
     *           within the Angular app after creating
     *           a case management session.
     *           CYPHER_COURT routes submit the
     *           cyAccess form to cypherCourt for
     *           auto login.
     *           When login payload omits preferences,
     *           loads them before choosing the route.
     * @access - public
     * @param  - string uId : user id
     * @return -
     */
    public async homepageProccessing
    (
        uId: string
    )
    {
        let homePreference: string;
        let routeTag: number;
        let navigateRoute: string;

        await this.preferenceService.loadPreferences(true);
        homePreference =
            this.preferenceService
                .getHomePreference();

        routeTag =
            this.preferenceService
                .getRouteTag(homePreference);

        if (routeTag === HOME_TAG.CYPHER_WEBAPP)
        {
            navigateRoute =
                this.getWebappRoute(
                    homePreference
                );

            if (!navigateRoute)
            {
                navigateRoute =
                    HOME_PAGE_ROUTE.DASHBOARD;
            }

            this.cyAccessService
                .createCaseManagementSession(uId)
            .subscribe(
            {
                next: () =>
                {
                    localStorage.setItem(
                        'activePage',
                        navigateRoute
                    );
                    this.router.navigate(
                        [`/${navigateRoute}`]
                    );
                },
                error: () =>
                {
                    localStorage.setItem(
                        'activePage',
                        navigateRoute
                    );
                    this.router.navigate(
                        [`/${navigateRoute}`]
                    );
                }
            });
        }
        else
        {
            this.submitCyAccess(uId);
        }
    }
}

export { HomepageService }